import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import {
  CheckCircle2, Loader2, Trash2, Gauge, RefreshCw, History, Play,
} from "lucide-react";
import SourceUploader from "./ai-textbook/SourceUploader";
import SettingsPanel from "./ai-textbook/SettingsPanel";
import DraftReview from "./ai-textbook/DraftReview";

function QuotaBadge({ quota, onRefresh }) {
  if (!quota) return null;
  const tone = quota.remaining === 0
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : quota.remaining <= 2
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
  const resetMin = Math.ceil((quota.reset_in_seconds || 0) / 60);
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${tone}`}
      title={quota.remaining === 0 && resetMin ? `Resets in ~${resetMin} min` : ""}
      data-testid="ai-quota-badge"
    >
      <Gauge className="w-3.5 h-3.5" />
      <span data-testid="ai-quota-text">
        {quota.remaining}/{quota.limit} generations left this hour
      </span>
      <button
        type="button"
        onClick={onRefresh}
        className="ml-1 opacity-70 hover:opacity-100"
        aria-label="Refresh quota"
        data-testid="ai-quota-refresh"
      >
        <RefreshCw className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function AiTextbook() {
  const nav = useNavigate();

  const [subject, setSubject] = useState("Physics");
  const [chapter, setChapter] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [standard, setStandard] = useState("JEE Advanced");
  const [count, setCount] = useState(5);
  const [rawText, setRawText] = useState("");
  const [file, setFile] = useState(null);
  const [keepSource, setKeepSource] = useState(false);

  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [banner, setBanner] = useState("");

  const [drafts, setDrafts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState({});
  const [edits, setEdits] = useState({});

  const [quota, setQuota] = useState(null);
  const [savedSources, setSavedSources] = useState([]);
  const [regenBusyId, setRegenBusyId] = useState(null);

  const [examOpen, setExamOpen] = useState(false);
  const [examBusy, setExamBusy] = useState(false);
  const [examForm, setExamForm] = useState({ title: "", duration_min: 30, passing_score: 50 });

  const refreshQuota = () =>
    api.get("/ai/quota").then((r) => setQuota(r.data)).catch(() => {});
  const refreshSources = () =>
    api.get("/ai/sources").then((r) => setSavedSources(r.data || [])).catch(() => setSavedSources([]));
  useEffect(() => { refreshQuota(); refreshSources(); }, []);

  const resetReview = () => {
    setDrafts([]); setMeta(null); setSelected({}); setEdits({});
  };

  const generate = async (e) => {
    e.preventDefault();
    setErr(""); setBanner("");
    if (!chapter.trim()) return setErr("Please add a chapter title.");
    if (!file && !rawText.trim()) return setErr("Upload a file or paste some source text first.");
    setBusy(true);
    resetReview();
    try {
      const fd = new FormData();
      fd.append("subject", subject.trim() || "General");
      fd.append("chapter_title", chapter.trim());
      fd.append("difficulty", difficulty);
      fd.append("exam_standard", standard);
      fd.append("num_questions", String(count));
      if (rawText.trim()) fd.append("raw_text", rawText.trim());
      if (file) fd.append("file", file);
      fd.append("keep_source", keepSource ? "true" : "false");
      const { data } = await api.post("/ai/generate-from-textbook", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      setDrafts(data.drafts || []);
      setMeta({ generated: data.generated, model: data.model, source_kind: data.source_kind });
      const sel = {};
      (data.drafts || []).forEach((d) => { sel[d.id] = true; });
      setSelected(sel);
      setExamForm({
        title: `${chapter.trim()} — Practice Set`,
        duration_min: Math.max(10, (data.drafts || []).length * 2),
        passing_score: 50,
      });
      refreshQuota();
      if (data.saved_source) refreshSources();
      else if (keepSource && data.storage_available === false) {
        setBanner("Questions generated, but the chapter couldn't be saved (storage is currently unavailable). Try again later to keep it.");
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const regenerateFromSource = async (src) => {
    setRegenBusyId(src.id); setErr(""); setBanner("");
    resetReview();
    try {
      const fd = new FormData();
      fd.append("num_questions", String(count));
      fd.append("difficulty", difficulty);
      const { data } = await api.post(`/ai/sources/${src.id}/regenerate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      setDrafts(data.drafts || []);
      setMeta({ generated: data.generated, model: data.model, source_kind: data.source_kind });
      const sel = {};
      (data.drafts || []).forEach((d) => { sel[d.id] = true; });
      setSelected(sel);
      setSubject(src.subject || subject);
      setChapter(src.chapter_title || chapter);
      setExamForm({
        title: `${src.chapter_title || "Chapter"} — Practice Set`,
        duration_min: Math.max(10, (data.drafts || []).length * 2),
        passing_score: 50,
      });
      refreshQuota(); refreshSources();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setRegenBusyId(null);
    }
  };

  const deleteSource = async (src) => {
    if (!window.confirm(`Remove "${src.chapter_title}" from your saved chapters?`)) return;
    try {
      await api.delete(`/ai/sources/${src.id}`);
      refreshSources();
    } catch (ex) {
      setErr(formatApiError(ex));
    }
  };

  const getEditFor = (d) => edits[d.id] || {
    text: d.text,
    options: [...(d.options || ["", "", "", ""])],
    correct_index: d.correct_index ?? 0,
    explanation: d.explanation || "",
  };
  const onEditText = (id, text) =>
    setEdits((e) => ({ ...e, [id]: { ...getEditFor({ id, ...(e[id] || {}) }), text } }));
  const onEditCorrect = (id, correct_index) =>
    setEdits((e) => ({ ...e, [id]: { ...getEditFor({ id, ...(e[id] || {}) }), correct_index } }));
  const onEditOption = (id, i, val) =>
    setEdits((e) => {
      const cur = getEditFor(drafts.find((d) => d.id === id) || { id });
      const options = [...(e[id]?.options || cur.options)];
      options[i] = val;
      return { ...e, [id]: { ...cur, ...(e[id] || {}), options } };
    });
  const onToggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const publishSelected = async () => {
    const chosen = drafts.filter((d) => selected[d.id]);
    if (chosen.length === 0) return setErr("Select at least one question to publish.");
    setSaving(true); setErr("");
    try {
      const editOps = chosen.filter((d) => edits[d.id]).map(async (d) => {
        const e = edits[d.id];
        await api.patch(`/question-bank/${d.id}`, {
          text: e.text, options: e.options, correct_index: e.correct_index,
        });
      });
      await Promise.all(editOps);
      const { data } = await api.post("/question-bank/publish", { ids: chosen.map((d) => d.id) });
      const rejected = drafts.filter((d) => !selected[d.id]).map((d) => d.id);
      if (rejected.length) await api.post("/question-bank/discard", { ids: rejected });
      setBanner(`Published ${data.published} question${data.published === 1 ? "" : "s"} to your Question Bank.`);
      resetReview();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const publishAsExam = async () => {
    const chosen = drafts.filter((d) => selected[d.id]);
    if (chosen.length === 0) return setErr("Select at least one question to include in the exam.");
    if (!examForm.title.trim()) return setErr("Give the new exam a title.");
    setExamBusy(true); setErr("");
    try {
      const editOps = chosen.filter((d) => edits[d.id]).map(async (d) => {
        const e = edits[d.id];
        await api.patch(`/question-bank/${d.id}`, {
          text: e.text, options: e.options, correct_index: e.correct_index,
        });
      });
      await Promise.all(editOps);
      const { data } = await api.post("/ai/publish-as-exam", {
        ids: chosen.map((d) => d.id),
        title: examForm.title.trim(),
        duration_min: Number(examForm.duration_min),
        passing_score: Number(examForm.passing_score),
        subject: subject || "General",
        description: `AI-generated set from "${chapter}" (${standard}, ${difficulty}).`,
      });
      const rejected = drafts.filter((d) => !selected[d.id]).map((d) => d.id);
      if (rejected.length) await api.post("/question-bank/discard", { ids: rejected });
      nav(`/teacher/exams/${data.exam.id}`);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setExamBusy(false);
    }
  };

  const discardAll = async () => {
    if (drafts.length === 0) return;
    if (!window.confirm("Discard all generated drafts? This cannot be undone.")) return;
    setSaving(true); setErr("");
    try {
      await api.post("/question-bank/discard", { ids: drafts.map((d) => d.id) });
      resetReview();
      setBanner("Discarded all drafts.");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const outOfQuota = !!(quota && quota.remaining === 0);

  return (
    <AppShell
      title="AI Textbook → Questions"
      subtitle="Upload a chapter or paste text, and let Gemini turn it into KaTeX-ready MCQs."
    >
      <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
        <QuotaBadge quota={quota} onRefresh={refreshQuota} />
      </div>
      {banner && (
        <div className="mb-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2 flex items-center gap-2" data-testid="ai-textbook-banner">
          <CheckCircle2 className="w-4 h-4" /> {banner}
        </div>
      )}
      {err && (
        <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2" data-testid="ai-textbook-error">
          {err}
        </div>
      )}

      {drafts.length === 0 ? (
        <>
          {savedSources.length > 0 && (
            <div className="qs-card p-5 mb-6" data-testid="ai-saved-sources">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-indigo-600" />
                <h3 className="font-display text-base font-bold text-slate-900">Your saved chapters</h3>
                <span className="text-xs text-slate-500">· click regenerate to run again without re-uploading</span>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {savedSources.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-200 transition"
                    data-testid={`ai-saved-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{s.chapter_title}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5 truncate" title={s.original_filename}>
                          {s.subject} · {s.original_filename}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1">
                          Saved {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                          {s.last_used_at && s.last_used_at !== s.created_at ? ` · reused ${new Date(s.last_used_at).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteSource(s)}
                        className="text-slate-400 hover:text-rose-600 shrink-0"
                        aria-label="Delete saved chapter"
                        data-testid={`ai-saved-delete-${s.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">
                        {s.source_kind}
                      </span>
                      <button
                        onClick={() => regenerateFromSource(s)}
                        disabled={regenBusyId === s.id || busy || outOfQuota}
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                        data-testid={`ai-saved-regenerate-${s.id}`}
                      >
                        {regenBusyId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        {regenBusyId === s.id ? "Regenerating…" : `Regenerate ${count}`}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={generate} className="grid lg:grid-cols-12 gap-6" data-testid="ai-textbook-form">
            <div className="lg:col-span-7 space-y-5">
              <SourceUploader
                file={file}
                onFileChange={setFile}
                rawText={rawText}
                onRawTextChange={setRawText}
                keepSource={keepSource}
                onKeepSourceChange={setKeepSource}
                onError={setErr}
              />
            </div>
            <div className="lg:col-span-5 space-y-5">
              <SettingsPanel
                subject={subject} onSubjectChange={setSubject}
                chapter={chapter} onChapterChange={setChapter}
                difficulty={difficulty} onDifficultyChange={setDifficulty}
                standard={standard} onStandardChange={setStandard}
                count={count} onCountChange={setCount}
                busy={busy}
                disabled={outOfQuota}
                disabledLabel="Hourly limit reached"
              />
            </div>
          </form>
        </>
      ) : (
        <DraftReview
          meta={meta}
          drafts={drafts}
          selected={selected}
          onToggleSelect={onToggleSelect}
          edits={edits}
          onEditText={onEditText}
          onEditCorrect={onEditCorrect}
          onEditOption={onEditOption}
          saving={saving}
          examBusy={examBusy}
          examOpen={examOpen}
          onToggleExamPanel={() => setExamOpen((o) => !o)}
          examForm={examForm}
          onExamFormChange={setExamForm}
          onPublish={publishSelected}
          onPublishAsExam={publishAsExam}
          onDiscardAll={discardAll}
          selectedCount={selectedCount}
        />
      )}
    </AppShell>
  );
}
